import { lazy, Suspense, useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import toast from 'react-hot-toast';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CityProvider } from './contexts/CityContext';
import { VenueLayout } from './components/VenueLayout';
import type { VenueResponse } from './features/venues/hooks/useVenues';
import { validatedApi } from './lib/api/validated-client';
import { CitySelectionModal } from './components/CitySelectionModal';
import { ToastProvider } from './features/admin/shared/context/ToastContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RoleProtectedRoute } from './components/RoleProtectedRoute';
import { FeatureGate } from './components/FeatureGate';
import { Role, parseError } from '@lov/shared';
import type { PaginatedVenuesResponse } from '@lov/shared';
import { AxiosError } from 'axios';
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  GoogleCallbackPage,
} from './pages';
// Customer/public pages - core first-load journey kept static
import { LandingPage } from './features/landing';
import { VenuesPage } from './features/venues/pages/VenuesPage';
import { VenueDetailPage } from './features/venues/pages/VenueDetailPage';
import { BookingBySlugPage } from './features/bookings/pages/BookingBySlugPage';
import { MyBookingsPage } from './features/bookings/pages/MyBookingsPage';
import { MainLayout, PublicLayout } from './components/layouts';
import { CookieConsent } from './components/CookieConsent';
import { UpdateNotification } from './components/UpdateNotification';
import { useVendorNotificationToasts } from './features/notifications/hooks/useVendorNotificationToasts';
import { useDesktopNotificationPermission } from './features/notifications/hooks/useDesktopNotificationPermission';
import { DesktopNotificationPrompt } from './features/notifications/components/DesktopNotificationPrompt';
import { ErrorBoundary } from './components/ErrorBoundary';
import {
  AdminLoadingFallback,
  VendorLoadingFallback,
  PageLoadingFallback,
  AnimatedSuspense,
} from './components/loading';
import { NotFoundPage } from './pages/NotFoundPage';
import { usePageTracking } from './hooks/usePageTracking';
// Heavy pages - lazy loaded to reduce initial bundle
const RoleDashboardRouter = lazy(() =>
  import('./pages/RoleDashboardRouter').then((m) => ({ default: m.RoleDashboardRouter }))
);
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage }))
);
const ContactPage = lazy(() =>
  import('./pages/ContactPage').then((m) => ({ default: m.ContactPage }))
);
// Admin pages - lazy loaded for code splitting
const AdminDashboardPage = lazy(() =>
  import('./features/analytics').then((m) => ({ default: m.AdminDashboardPage }))
);
const AdminUsersPage = lazy(() =>
  import('./features/admin/users').then((m) => ({ default: m.AdminUsersPage }))
);
const AdminUserDetailPage = lazy(() =>
  import('./features/admin/users').then((m) => ({ default: m.AdminUserDetailPage }))
);
const VendorApprovalPage = lazy(() =>
  import('./features/admin/pages').then((m) => ({ default: m.VendorApprovalPage }))
);
const VendorEnquiriesPage = lazy(() =>
  import('./features/admin/pages/VendorEnquiriesPage').then((m) => ({
    default: m.VendorEnquiriesPage,
  }))
);
const AdminVendorsPage = lazy(() =>
  import('./features/admin/pages').then((m) => ({ default: m.AdminVendorsPage }))
);
const AdminVenuesListPage = lazy(() =>
  import('./features/admin/pages').then((m) => ({ default: m.AdminVenuesListPage }))
);
const AdminSupportPage = lazy(() =>
  import('./features/admin/pages').then((m) => ({ default: m.AdminSupportPage }))
);
const LocationManagementPage = lazy(() =>
  import('./features/admin/pages').then((m) => ({ default: m.LocationManagementPage }))
);
const MasterDataPage = lazy(() =>
  import('./features/admin/pages').then((m) => ({ default: m.MasterDataPage }))
);
const ReferralConfigPage = lazy(() =>
  import('./features/admin/pages').then((m) => ({ default: m.ReferralConfigPage }))
);
const AdminInventoryPage = lazy(() =>
  import('./features/admin/inventory').then((m) => ({ default: m.AdminInventoryPage }))
);
const AdminInventoryRequestsPage = lazy(() =>
  import('./features/admin/inventory').then((m) => ({ default: m.AdminInventoryRequestsPage }))
);
const AdminCouponsPage = lazy(() =>
  import('./features/admin/coupons').then((m) => ({ default: m.AdminCouponsPage }))
);
const AdminStaffPage = lazy(() =>
  import('./features/staff/pages').then((m) => ({ default: m.AdminStaffPage }))
);
const AdminVendorStaffPage = lazy(() =>
  import('./features/admin/pages').then((m) => ({ default: m.AdminVendorStaffPage }))
);
const PlansPage = lazy(() =>
  import('./features/admin/pages/PlansPage').then((m) => ({ default: m.PlansPage }))
);
const AdminModulesPage = lazy(() =>
  import('./features/admin/pages/AdminModulesPage').then((m) => ({ default: m.AdminModulesPage }))
);
const CapabilityMigrationPage = lazy(() =>
  import('./features/admin/pages/CapabilityMigrationPage').then((m) => ({
    default: m.CapabilityMigrationPage,
  }))
);
const CapabilityHealthPage = lazy(() =>
  import('./features/admin/subscriptions/pages/CapabilityHealthPage').then((m) => ({
    default: m.CapabilityHealthPage,
  }))
);
const FeatureFlagsPage = lazy(() =>
  import('./features/feature-flags').then((m) => ({ default: m.FeatureFlagsPage }))
);
const FeatureFlagDetailPage = lazy(() =>
  import('./features/feature-flags').then((m) => ({ default: m.FeatureFlagDetailPage }))
);
const SystemSettingsPage = lazy(() =>
  import('./features/settings').then((m) => ({ default: m.SystemSettingsPage }))
);
const SettingsAuditLogPage = lazy(() =>
  import('./features/settings').then((m) => ({ default: m.SettingsAuditLogPage }))
);
const RealtimeAnalyticsPage = lazy(() =>
  import('./features/analytics').then((m) => ({ default: m.RealtimeAnalyticsPage }))
);
const FinancialAnalyticsPage = lazy(() =>
  import('./features/analytics').then((m) => ({ default: m.FinancialAnalyticsPage }))
);
const RevenueReportsPage = lazy(() =>
  import('./features/analytics').then((m) => ({ default: m.RevenueReportsPage }))
);
const UserMetricsPage = lazy(() =>
  import('./features/analytics').then((m) => ({ default: m.UserMetricsPage }))
);
const VenuePerformancePage = lazy(() =>
  import('./features/analytics').then((m) => ({ default: m.VenuePerformancePage }))
);
const FoodCategoriesPage = lazy(() =>
  import('./features/food/admin/pages/FoodCategoriesPage').then((m) => ({
    default: m.FoodCategoriesPage,
  }))
);
const FoodOrderPage = lazy(() =>
  import('./features/food/customer/pages/FoodOrderPage').then((m) => ({
    default: m.FoodOrderPage,
  }))
);
const AdminCreateVenuePage = lazy(() =>
  import('./features/venues/pages').then((m) => ({ default: m.AdminCreateVenuePage }))
);
const AdminVenueApprovalPage = lazy(() =>
  import('./features/venues/pages').then((m) => ({ default: m.AdminVenueApprovalPage }))
);
const AdminBookingsPage = lazy(() =>
  import('./features/admin/pages').then((m) => ({ default: m.AdminBookingsPage }))
);
const AdminSubscriptionsPage = lazy(() =>
  import('./features/admin/subscriptions/pages').then((m) => ({
    default: m.AdminSubscriptionsPage,
  }))
);
const AdminSubscriptionDetailPage = lazy(() =>
  import('./features/admin/subscriptions/pages').then((m) => ({
    default: m.SubscriptionDetailPage,
  }))
);
const AdminSubscriptionAnalyticsPage = lazy(() =>
  import('./features/admin/subscriptions/pages').then((m) => ({
    default: m.AdminSubscriptionAnalyticsPage,
  }))
);
const CheckInConfigPage = lazy(() =>
  import('./pages/admin/check-in-config').then((m) => ({ default: m.default }))
);
const CronDashboardPage = lazy(() =>
  import('./features/admin/cron-monitoring').then((m) => ({ default: m.CronDashboardPage }))
);
const CronDetailPage = lazy(() =>
  import('./features/admin/cron-monitoring').then((m) => ({ default: m.CronDetailPage }))
);
const SslManagementPage = lazy(() =>
  import('./features/admin/ssl').then((m) => ({ default: m.SslManagementPage }))
);
// CityVenuePage — SEO landing pages for /discover/:venueType/:citySlug
const CityVenuePage = lazy(() =>
  import('./features/seo/pages/CityVenuePage').then((m) => ({ default: m.CityVenuePage }))
);
const PCAvailabilityPage = lazy(() =>
  import('./features/venues/pages/PCAvailabilityPage').then((m) => ({
    default: m.PCAvailabilityPage,
  }))
);
// ListYourVenuePage has its own lazy import so it does not share a chunk with vendor
// dashboard pages (which require authentication). This prevents the VendorLoadingFallback
// — "Loading vendor dashboard..." — from showing to unauthenticated visitors on a
// public marketing page, and avoids the infinite spinner when vendor chunks fail.
const ListYourVenuePage = lazy(() =>
  import('./features/vendor/pages/ListYourVenuePage').then((m) => ({
    default: m.ListYourVenuePage,
  }))
);
const VendorDashboard = lazy(() =>
  import('./features/vendor/pages').then((m) => ({ default: m.VendorDashboard }))
);
const VendorSettingsPage = lazy(() =>
  import('./features/vendor/pages').then((m) => ({ default: m.VendorSettingsPage }))
);
const VendorReviewsPage = lazy(() =>
  import('./features/reviews/pages').then((m) => ({ default: m.VendorReviewsPage }))
);
const VendorLeaderboardPage = lazy(() =>
  import('./features/vendor/pages').then((m) => ({ default: m.VendorLeaderboardPage }))
);
const VendorVenuesPage = lazy(() =>
  import('./features/venues/pages').then((m) => ({ default: m.VendorVenuesPage }))
);
const VendorVenueDetailPage = lazy(() =>
  import('./features/venues/pages').then((m) => ({ default: m.VendorVenueDetailPage }))
);
const VendorBookingsListPage = lazy(() =>
  import('./features/bookings/pages').then((m) => ({ default: m.VendorBookingsListPage }))
);
const VendorBookingsCalendarPage = lazy(() =>
  import('./features/bookings/pages').then((m) => ({ default: m.VendorBookingsCalendarPage }))
);
const UnpaidBookingsPage = lazy(() =>
  import('./features/bookings/pages').then((m) => ({ default: m.UnpaidBookingsPage }))
);
const VendorCheckInPage = lazy(() =>
  import('./features/bookings/pages').then((m) => ({ default: m.VendorCheckInPage }))
);
const ActiveSessionsPageWrapper = lazy(() =>
  import('./features/bookings/pages').then((m) => ({ default: m.ActiveSessionsPageWrapper }))
);
const VendorRequestInventoryPage = lazy(() =>
  import('./features/inventory/pages').then((m) => ({ default: m.VendorRequestInventoryPage }))
);
const VenueInventoryPage = lazy(() =>
  import('./features/inventory/pages').then((m) => ({ default: m.VenueInventoryPage }))
);
const VenueInventoryRedirectPage = lazy(() =>
  import('./features/inventory/pages').then((m) => ({ default: m.VenueInventoryRedirectPage }))
);
const InventoryAddonConfigPage = lazy(() =>
  import('./features/vendor/pages/InventoryAddonConfig').then((m) => ({
    default: m.InventoryAddonConfig,
  }))
);
const VendorCouponPage = lazy(() =>
  import('./features/coupons/pages').then((m) => ({ default: m.VendorCouponPage }))
);
const StaffManagementPage = lazy(() =>
  import('./features/staff/pages').then((m) => ({ default: m.StaffManagementPage }))
);
const VendorAnalyticsDashboardPage = lazy(() =>
  import('./features/analytics').then((m) => ({ default: m.VendorAnalyticsDashboardPage }))
);
const AddonAnalyticsPage = lazy(() =>
  import('./features/vendor/pages').then((m) => ({ default: m.AddonAnalytics }))
);
// VendorUsersPage removed - /vendor/users redirects to /vendor/customers
const VendorCustomersPage = lazy(() => import('./pages/vendor/VendorCustomersPage'));
const VendorBookingDetailPage = lazy(() =>
  import('./features/bookings/pages/VendorBookingDetailPage').then((m) => ({
    default: m.VendorBookingDetailPage,
  }))
);
const VendorCustomerDetailPage = lazy(() => import('./pages/vendor/VendorCustomerDetailPage'));
const VendorMenuPage = lazy(() =>
  import('./features/food/vendor/pages/VendorMenuPage').then((m) => ({ default: m.VendorMenuPage }))
);
const VendorOffersPage = lazy(() =>
  import('./features/food/vendor/pages/VendorOffersPage').then((m) => ({
    default: m.VendorOffersPage,
  }))
);
const FoodAnalyticsPage = lazy(() =>
  import('./features/vendor/pages/FoodAnalytics').then((m) => ({ default: m.FoodAnalytics }))
);
// VendorInvoicesPage removed - invoices tab disabled for vendor dashboard;
const TaxInvoicesPage = lazy(() =>
  import('./features/tax-invoices').then((m) => ({ default: m.TaxInvoicesPage }))
);
const SubscriptionPlansPage = lazy(() =>
  import('./features/vendor/subscription/pages/SubscriptionPlansPage').then((m) => ({
    default: m.SubscriptionPlansPage,
  }))
);
const SubscriptionCheckoutPage = lazy(() =>
  import('./features/vendor/subscription/pages/SubscriptionCheckoutPage').then((m) => ({
    default: m.SubscriptionCheckoutPage,
  }))
);
const BillingHistoryPage = lazy(() =>
  import('./features/vendor/subscription/pages/BillingHistoryPage').then((m) => ({
    default: m.BillingHistoryPage,
  }))
);
const VendorTaxSummaryPage = lazy(() =>
  import('./features/analytics/pages/VendorTaxSummaryPage').then((m) => ({
    default: m.VendorTaxSummaryPage,
  }))
);
const PCAnalyticsPage = lazy(() =>
  import('./features/analytics/pages/PCAnalyticsPage').then((m) => ({
    default: m.PCAnalyticsPage,
  }))
);
const AgentDevicesPage = lazy(() =>
  import('./features/agent/pages/AgentDevicesPage').then((m) => ({
    default: m.AgentDevicesPage,
  }))
);
const PCUnlockPage = lazy(() =>
  import('./features/agent/pages/PCUnlockPage').then((m) => ({ default: m.PCUnlockPage }))
);
const PCExtendPage = lazy(() =>
  import('./features/agent/pages/PCExtendPage').then((m) => ({ default: m.PCExtendPage }))
);
const PCAvailabilityGrid = lazy(() =>
  import('./features/agent/pages/PCAvailabilityGrid').then((m) => ({
    default: m.PCAvailabilityGrid,
  }))
);
const CommissionInvoicesPage = lazy(() =>
  import('./features/commission-invoices').then((m) => ({ default: m.CommissionInvoicesPage }))
);
const AdminInvoicesPage = lazy(() =>
  import('./features/invoices').then((m) => ({ default: m.AdminInvoicesPage }))
);
const MyInvoicesPage = lazy(() =>
  import('./features/invoices').then((m) => ({ default: m.MyInvoicesPage }))
);
const InvoiceDetailPage = lazy(() =>
  import('./features/invoices').then((m) => ({ default: m.InvoiceDetailPage }))
);
const VendorPayoutsPage = lazy(() =>
  import('./features/payouts/vendor/pages').then((m) => ({ default: m.VendorPayoutsPage }))
);
const VendorPayoutDetailPage = lazy(() =>
  import('./features/payouts/vendor/pages').then((m) => ({ default: m.VendorPayoutDetailPage }))
);
const AdminPayoutsPage = lazy(() =>
  import('./features/payouts/admin/pages').then((m) => ({ default: m.AdminPayoutsPage }))
);
const PayoutHealthDashboard = lazy(() =>
  import('./features/admin/payouts/pages').then((m) => ({ default: m.PayoutsDashboardPage }))
);
const ReferralDashboardPage = lazy(() =>
  import('./features/referral/pages').then((m) => ({ default: m.ReferralDashboardPage }))
);
const LoyaltyDashboardPage = lazy(() =>
  import('./features/loyalty/pages').then((m) => ({ default: m.LoyaltyDashboardPage }))
);
const CompetitiveProfilePage = lazy(() =>
  import('./features/competitive/pages/CompetitiveProfilePage').then((m) => ({
    default: m.CompetitiveProfilePage,
  }))
);
const VenueLeaderboardPage = lazy(() =>
  import('./features/venues/pages/VenueLeaderboardPage').then((m) => ({
    default: m.VenueLeaderboardPage,
  }))
);
const BadgeCollectionPage = lazy(() =>
  import('./features/loyalty/pages').then((m) => ({ default: m.BadgeCollectionPage }))
);

// Customer pages - lazy loaded (non-critical-path)
const BookingDetailPage = lazy(() =>
  import('./features/bookings/pages/BookingDetailPage').then((m) => ({
    default: m.BookingDetailPage,
  }))
);
const BookingConfirmationPage = lazy(() =>
  import('./features/bookings/pages/BookingConfirmationPage').then((m) => ({
    default: m.BookingConfirmationPage,
  }))
);
const CustomerActiveSessionPage = lazy(() =>
  import('./features/bookings/pages/CustomerActiveSessionPage').then((m) => ({
    default: m.CustomerActiveSessionPage,
  }))
);
const PaymentPage = lazy(() =>
  import('./features/payments/pages/PaymentPage').then((m) => ({ default: m.PaymentPage }))
);
const PaymentStatusPage = lazy(() =>
  import('./features/payments/pages/PaymentStatusPage').then((m) => ({
    default: m.PaymentStatusPage,
  }))
);
const PaymentMethodSelectionPage = lazy(() =>
  import('./features/payments/pages/PaymentMethodSelectionPage').then((m) => ({
    default: m.PaymentMethodSelectionPage,
  }))
);
const MockPaymentGatewayPage = lazy(
  () => import('./features/payments/pages/MockPaymentGatewayPage')
);
const StagingMockPaymentPage = lazy(
  () => import('./features/payments/pages/StagingMockPaymentPage')
);
const AcceptInvitationPage = lazy(() =>
  import('./features/staff/pages/AcceptInvitationPage').then((m) => ({
    default: m.AcceptInvitationPage,
  }))
);
const NotificationsPage = lazy(() =>
  import('./features/notifications').then((m) => ({ default: m.NotificationsPage }))
);
const FavoritesPage = lazy(() =>
  import('./features/favorites').then((m) => ({ default: m.FavoritesPage }))
);
const WalletPage = lazy(() => import('./features/wallet').then((m) => ({ default: m.WalletPage })));
const LordCoinsPage = lazy(() =>
  import('./features/lord-coins').then((m) => ({ default: m.LordCoinsPage }))
);
const TermsPage = lazy(() => import('./pages/legal').then((m) => ({ default: m.TermsPage })));
const PrivacyPage = lazy(() => import('./pages/legal').then((m) => ({ default: m.PrivacyPage })));
const RefundPolicyPage = lazy(() =>
  import('./pages/legal').then((m) => ({ default: m.RefundPolicyPage }))
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes — keep inactive query cache for back/forward nav
      refetchOnWindowFocus: false, // Stop refetch storms on tab switch
      retry: (failureCount, error) => {
        // Never retry 401/403 — the API interceptor handles 401 refresh; 403 is not recoverable
        if (
          error instanceof AxiosError &&
          (error.response?.status === 401 || error.response?.status === 403)
        ) {
          return false;
        }
        return failureCount < 1;
      },
    },
    mutations: {
      onError: (error) => {
        const parsed = parseError(error);
        toast.error(parsed.message);
      },
    },
  },
});

// Suspense wrapper helpers for route groups.
// AnimatedSuspense adds a 200 ms fade-out to the fallback when content loads,
// completing the delight arc started by the entrance animation on SceneContainer.
const withAdminSuspense = (element: JSX.Element) => (
  <AnimatedSuspense fallback={<AdminLoadingFallback />}>{element}</AnimatedSuspense>
);

const withVendorSuspense = (element: JSX.Element) => (
  <AnimatedSuspense fallback={<VendorLoadingFallback />}>{element}</AnimatedSuspense>
);

const withPageSuspense = (element: JSX.Element) => (
  <AnimatedSuspense fallback={<PageLoadingFallback />}>{element}</AnimatedSuspense>
);

// Route prefetching component - preloads admin/vendor routes based on user role
function RoutePrefetcher() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    // Prefetch admin routes for SUPER_ADMIN users
    if (user.role === Role.SUPER_ADMIN) {
      // Prefetch critical admin pages after a short delay (non-blocking)
      const prefetchTimer = setTimeout(() => {
        Promise.all([
          import('./features/admin/pages'),
          import('./features/inventory/pages'),
          import('./features/coupons/pages'),
        ]).catch(() => {
          // Silently fail - prefetching is non-critical
        });
      }, 1000);

      return () => clearTimeout(prefetchTimer);
    }

    // Prefetch vendor routes for VENDOR/VENDOR_STAFF users
    if (user.role === Role.VENDOR || user.role === Role.VENDOR_STAFF) {
      // Prefetch critical vendor pages after a short delay (non-blocking)
      const prefetchTimer = setTimeout(() => {
        Promise.all([
          import('./features/vendor/pages'),
          import('./features/bookings/pages'),
          import('./features/venues/pages'),
        ]).catch(() => {
          // Silently fail - prefetching is non-critical
        });
      }, 1000);

      return () => clearTimeout(prefetchTimer);
    }
  }, [user]);

  return null;
}

// Analytics page tracking component - tracks page views on route changes
function PageTracker() {
  usePageTracking();
  return null;
}

// Legacy redirect component: reads first venue and redirects to /vendor/:venueId/<to>
function VenueRedirect({ to }: { to: string }) {
  const { data: venues, isLoading } = useQuery<PaginatedVenuesResponse, Error, VenueResponse[]>({
    queryKey: ['venues', 'my'],
    queryFn: () => validatedApi.get('/venues/my'),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });
  if (isLoading) return <VendorLoadingFallback />;
  const venueId = venues?.[0]?.id;
  if (!venueId) return <Navigate to="/vendor/venues" replace />;
  return <Navigate to={`/vendor/${venueId}/${to}`} replace />;
}

// Legacy redirect for parameterized booking detail path: /vendor/bookings/:bookingId → /vendor/:venueId/bookings/:bookingId
function BookingDetailVenueRedirect() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const { data: venues, isLoading } = useQuery<PaginatedVenuesResponse, Error, VenueResponse[]>({
    queryKey: ['venues', 'my'],
    queryFn: () => validatedApi.get('/venues/my'),
    select: (r) => r.data,
    staleTime: 5 * 60 * 1000,
  });
  if (isLoading) return <VendorLoadingFallback />;
  const venueId = venues?.[0]?.id;
  if (!venueId) return <Navigate to="/vendor/venues" replace />;
  return <Navigate to={`/vendor/${venueId}/bookings/${bookingId}`} replace />;
}

// Mounts vendor toast notifications. Must be inside BrowserRouter + AuthProvider + QueryClientProvider.
function VendorNotificationToasts() {
  useVendorNotificationToasts();
  return null;
}

// Shows desktop notification permission banner for vendor roles.
// Waits for CookieConsent to be resolved before mounting so the two banners
// never overlap. Must be inside BrowserRouter + AuthProvider.
function VendorDesktopNotificationPrompt() {
  const { user } = useAuth();
  const { shouldShowPrompt, requestPermission, dismiss, sessionDismiss } =
    useDesktopNotificationPermission();
  const [cookieResolved, setCookieResolved] = useState(
    () => typeof window !== 'undefined' && !!localStorage.getItem('lov_cookie_consent')
  );

  useEffect(() => {
    if (cookieResolved) return;
    const id = setInterval(() => {
      if (localStorage.getItem('lov_cookie_consent')) {
        setCookieResolved(true);
        clearInterval(id);
      }
    }, 500);
    return () => clearInterval(id);
  }, [cookieResolved]);

  const isVendor = user?.role === Role.VENDOR || user?.role === Role.VENDOR_STAFF;
  if (!isVendor || !cookieResolved) return null;

  return (
    <DesktopNotificationPrompt
      open={shouldShowPrompt}
      onAllow={requestPermission}
      onDismiss={dismiss}
      onSessionDismiss={sessionDismiss}
    />
  );
}

function App() {
  return (
    <ThemeContextProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AuthProvider>
              <CityProvider>
                <CitySelectionModal />
                <ToastProvider>
                  <RoutePrefetcher />
                  <PageTracker />
                  <ErrorBoundary>
                    <Routes>
                      {/* Public Routes - Auth Pages */}
                      <Route
                        path="/login"
                        element={
                          <PublicLayout>
                            <LoginPage />
                          </PublicLayout>
                        }
                      />
                      <Route
                        path="/register"
                        element={
                          <PublicLayout>
                            <RegisterPage />
                          </PublicLayout>
                        }
                      />
                      <Route
                        path="/forgot-password"
                        element={
                          <PublicLayout>
                            <ForgotPasswordPage />
                          </PublicLayout>
                        }
                      />
                      <Route
                        path="/reset-password"
                        element={
                          <PublicLayout>
                            <ResetPasswordPage />
                          </PublicLayout>
                        }
                      />
                      <Route
                        path="/verify-email"
                        element={
                          <PublicLayout>
                            <VerifyEmailPage />
                          </PublicLayout>
                        }
                      />
                      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />

                      {/* Public Staff Invitation Route */}
                      <Route
                        path="/staff/invitation/accept"
                        element={
                          <PublicLayout>
                            <AcceptInvitationPage />
                          </PublicLayout>
                        }
                      />

                      {/* Public Venue Enquiry — no auth required, use a neutral spinner fallback */}
                      <Route
                        path="/list-your-venue"
                        element={
                          <PublicLayout>
                            <Suspense
                              fallback={
                                <div className="min-h-screen flex items-center justify-center">
                                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
                                </div>
                              }
                            >
                              <ListYourVenuePage />
                            </Suspense>
                          </PublicLayout>
                        }
                      />

                      {/* Public Venue Routes — pages manage their own layout via MainLayout */}
                      <Route path="/venues" element={<VenuesPage />} />
                      {/* SEO city landing pages — must come before /:stateSlug/:citySlug/:venueSlug */}
                      <Route
                        path="/discover/:venueType/:citySlug"
                        element={withPageSuspense(<CityVenuePage />)}
                      />
                      {/* SEO-friendly venue URL: /state/city/venue-slug */}
                      <Route
                        path="/:stateSlug/:citySlug/:venueSlug"
                        element={<VenueDetailPage />}
                      />
                      <Route
                        path="/:stateSlug/:citySlug/:venueSlug/book"
                        element={
                          <ProtectedRoute>
                            <PublicLayout>
                              <BookingBySlugPage />
                            </PublicLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/:stateSlug/:citySlug/:venueSlug/pcs"
                        element={withPageSuspense(<PCAvailabilityPage />)}
                      />

                      {/* Legal Pages - Public */}
                      <Route
                        path="/terms"
                        element={
                          <PublicLayout>
                            <TermsPage />
                          </PublicLayout>
                        }
                      />
                      <Route
                        path="/privacy"
                        element={
                          <PublicLayout>
                            <PrivacyPage />
                          </PublicLayout>
                        }
                      />
                      <Route
                        path="/refund-policy"
                        element={
                          <PublicLayout>
                            <RefundPolicyPage />
                          </PublicLayout>
                        }
                      />
                      <Route
                        path="/contact"
                        element={withPageSuspense(
                          <PublicLayout>
                            <ContactPage />
                          </PublicLayout>
                        )}
                      />

                      {/* Protected Routes */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            {withPageSuspense(<RoleDashboardRouter />)}
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>{withPageSuspense(<ProfilePage />)}</ProtectedRoute>
                        }
                      />
                      <Route
                        path="/notifications"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <NotificationsPage />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Customer Booking Routes */}
                      <Route
                        path="/bookings"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <MyBookingsPage />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/bookings/:id"
                        element={
                          <ProtectedRoute>
                            <BookingDetailPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/bookings/:id/session"
                        element={
                          <ProtectedRoute>
                            <AnimatedSuspense fallback={<PageLoadingFallback />}>
                              <CustomerActiveSessionPage />
                            </AnimatedSuspense>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/bookings/:id/confirmation"
                        element={
                          <ProtectedRoute>
                            <BookingConfirmationPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/bookings/:id/food"
                        element={
                          <ProtectedRoute>{withPageSuspense(<FoodOrderPage />)}</ProtectedRoute>
                        }
                      />

                      {/* Customer Invoice Routes */}
                      <Route
                        path="/invoices"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <MyInvoicesPage />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/invoices/:id"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <InvoiceDetailPage />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Customer Referral Routes */}
                      <Route
                        path="/referrals"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <Suspense fallback={<div>Loading...</div>}>
                                <ReferralDashboardPage />
                              </Suspense>
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Customer Loyalty Routes */}
                      <Route
                        path="/loyalty"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <Suspense fallback={<div>Loading...</div>}>
                                <LoyaltyDashboardPage />
                              </Suspense>
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/badges"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <Suspense fallback={<div>Loading...</div>}>
                                <BadgeCollectionPage />
                              </Suspense>
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Customer Favourites Route */}
                      <Route
                        path="/venues/favourites"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <Suspense fallback={<div>Loading...</div>}>
                                <FavoritesPage />
                              </Suspense>
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Customer Wallet Route */}
                      <Route
                        path="/wallet"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <WalletPage />
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Lord Coins Wallet Route */}
                      <Route
                        path="/lord-coins"
                        element={
                          <ProtectedRoute>
                            <FeatureGate
                              flag="lord-coins-wallet"
                              fallback={<Navigate to="/" replace />}
                            >
                              <MainLayout>
                                <LordCoinsPage />
                              </MainLayout>
                            </FeatureGate>
                          </ProtectedRoute>
                        }
                      />

                      {/* PC Unlock — public route; auth handled inside the page */}
                      <Route
                        path="/pc-unlock/:unitId"
                        element={withPageSuspense(<PCUnlockPage />)}
                      />

                      {/* PC Session Extension — public route; auth handled inside the page */}
                      <Route
                        path="/pc-extend/:bookingId"
                        element={withPageSuspense(<PCExtendPage />)}
                      />

                      {/* Payment Routes */}
                      {/* Payment Method Selection */}
                      <Route
                        path="/payments/select"
                        element={
                          <ProtectedRoute>
                            <PaymentMethodSelectionPage />
                          </ProtectedRoute>
                        }
                      />
                      {/* Legacy UPI route — redirect to unified checkout */}
                      <Route
                        path="/payments/upi"
                        element={<Navigate to="/payments/select" replace />}
                      />
                      {/* ICICI Card/Net Banking Payment */}
                      <Route
                        path="/payments/icici"
                        element={
                          <ProtectedRoute>
                            <PaymentPage />
                          </ProtectedRoute>
                        }
                      />
                      {/* Legacy /payment route - redirect to /payments/icici */}
                      <Route
                        path="/payment"
                        element={
                          <ProtectedRoute>
                            <PaymentPage />
                          </ProtectedRoute>
                        }
                      />
                      {/* Payment Status */}
                      <Route path="/payment/status" element={<PaymentStatusPage />} />
                      {/* Mock Payment Gateway (Dev Only) */}
                      <Route
                        path="/payments/mock-gateway"
                        element={
                          import.meta.env.DEV ? (
                            <Suspense fallback={<div>Loading...</div>}>
                              <MockPaymentGatewayPage />
                            </Suspense>
                          ) : (
                            <ProtectedRoute>
                              <RoleDashboardRouter />
                            </ProtectedRoute>
                          )
                        }
                      />

                      {/* Competitive Leaderboard Routes */}
                      <Route
                        path="/venues/:venueId/competitive"
                        element={
                          <ProtectedRoute>
                            <FeatureGate flag="competitive-leaderboard">
                              <MainLayout>
                                <Suspense fallback={<div>Loading...</div>}>
                                  <CompetitiveProfilePage />
                                </Suspense>
                              </MainLayout>
                            </FeatureGate>
                          </ProtectedRoute>
                        }
                      />
                      {/* Venue Leaderboard (customer-facing) */}
                      <Route
                        path="/venues/:venueId/leaderboard"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <Suspense fallback={<div>Loading...</div>}>
                                <VenueLeaderboardPage />
                              </Suspense>
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* Customer Loyalty Routes */}
                      <Route
                        path="/loyalty"
                        element={
                          <ProtectedRoute>
                            <MainLayout>
                              <Suspense fallback={<div>Loading...</div>}>
                                <LoyaltyDashboardPage />
                              </Suspense>
                            </MainLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* Staging Mock Payment (Staging Only) */}
                      {import.meta.env.VITE_ENV === 'staging' && (
                        <Route
                          path="/payments/staging-mock"
                          element={
                            <ProtectedRoute>
                              <Suspense fallback={<div>Loading...</div>}>
                                <StagingMockPaymentPage />
                              </Suspense>
                            </ProtectedRoute>
                          }
                        />
                      )}

                      {/* Admin Routes - SUPER_ADMIN only */}
                      <Route
                        path="/admin/dashboard"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminDashboardPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/bookings"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminBookingsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/users"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminUsersPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/users/:id"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminUserDetailPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/vendors"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminVendorsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/vendors/:vendorId/staff"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminVendorStaffPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/vendors/pending"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<VendorApprovalPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/vendor-enquiries"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<VendorEnquiriesPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/locations"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<LocationManagementPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/master-data"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<MasterDataPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/referral/configs"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<ReferralConfigPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/payouts/health"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<PayoutHealthDashboard />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/payouts"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminPayoutsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Cron Monitoring Routes */}
                      <Route
                        path="/admin/cron-monitoring"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<CronDashboardPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/cron-monitoring/:name"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<CronDetailPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      <Route
                        path="/admin/ssl"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<SslManagementPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      <Route
                        path="/admin/venues"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminVenuesListPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/venues/create"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminCreateVenuePage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/venues/pending"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminVenueApprovalPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Inventory Routes */}
                      <Route
                        path="/admin/inventory"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminInventoryPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/inventory/requests"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>
                              {withAdminSuspense(<AdminInventoryRequestsPage />)}
                            </MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Coupon Routes */}
                      <Route
                        path="/admin/coupons"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminCouponsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Support Tickets */}
                      <Route
                        path="/admin/support"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminSupportPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Staff Routes */}
                      <Route
                        path="/admin/staff"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminStaffPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Subscription Plans Routes */}
                      <Route
                        path="/admin/subscription-plans"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<PlansPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Module & Capability Management Routes */}
                      <Route
                        path="/admin/modules"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminModulesPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/migration/capabilities"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>
                              {withAdminSuspense(<CapabilityMigrationPage />)}
                            </MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      <Route
                        path="/admin/analytics/capabilities"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<CapabilityHealthPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Feature Flags Routes */}
                      <Route
                        path="/admin/feature-flags"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<FeatureFlagsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/feature-flags/:key"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<FeatureFlagDetailPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Food Management Routes */}
                      <Route
                        path="/admin/food-categories"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<FoodCategoriesPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin System Settings Routes */}
                      <Route
                        path="/admin/settings"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<SystemSettingsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/settings/audit-log"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<SettingsAuditLogPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Analytics Routes */}
                      <Route
                        path="/admin/analytics"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminDashboardPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/analytics/realtime"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<RealtimeAnalyticsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/analytics/financial"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<FinancialAnalyticsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/analytics/revenue"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<RevenueReportsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/analytics/users"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<UserMetricsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/analytics/venues"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<VenuePerformancePage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/analytics/venues/:venueId"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<VenuePerformancePage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/subscriptions"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminSubscriptionsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/subscriptions/:id"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>
                              {withAdminSuspense(<AdminSubscriptionDetailPage />)}
                            </MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/analytics/subscriptions"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>
                              {withAdminSuspense(<AdminSubscriptionAnalyticsPage />)}
                            </MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Check-In Config Route */}
                      <Route
                        path="/admin/check-in-config"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<CheckInConfigPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Admin Invoice Routes */}
                      <Route
                        path="/admin/invoices"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.SUPER_ADMIN]}>
                            <MainLayout>{withAdminSuspense(<AdminInvoicesPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* ── Vendor Routes ─────────────────────────────────── */}

                      {/* /vendor/login → /login redirect */}
                      <Route path="/vendor/login" element={<Navigate to="/login" replace />} />

                      {/* Reserve /vendor/all/* — legacy "All Venues" mode is gone */}
                      <Route
                        path="/vendor/all/*"
                        element={<Navigate to="/vendor/venues" replace />}
                      />

                      {/* Vendor-level routes (no venueId) */}

                      {/* Vendor Venue Detail (venue management) */}
                      <Route
                        path="/vendor/venues/:venueId"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>{withVendorSuspense(<VendorVenueDetailPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Vendor Venue inventory (venue management) */}
                      <Route
                        path="/vendor/venues/inventory"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>
                              {withVendorSuspense(<VenueInventoryRedirectPage />)}
                            </MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/vendor/venues/:venueId/inventory"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>{withVendorSuspense(<VenueInventoryPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/vendor/venues/:venueId/menu"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>{withVendorSuspense(<VendorMenuPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/vendor/venues/:venueId/offers"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>{withVendorSuspense(<VendorOffersPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Vendor Venues list */}
                      <Route
                        path="/vendor/venues"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>{withVendorSuspense(<VendorVenuesPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Vendor Inventory Requests */}
                      <Route
                        path="/vendor/inventory/requests"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>
                              {withVendorSuspense(<VendorRequestInventoryPage />)}
                            </MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/vendor/inventory/:venueInventoryId/addons"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>
                              {withVendorSuspense(<InventoryAddonConfigPage />)}
                            </MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Vendor Settings */}
                      <Route
                        path="/vendor/settings"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>{withVendorSuspense(<VendorSettingsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Vendor Subscription Plans */}
                      <Route
                        path="/vendor/subscription/plans"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR]}>
                            <FeatureGate
                              flag="vendor-billing"
                              fallback={<Navigate to="/vendor/venues" replace />}
                            >
                              <MainLayout>
                                {withVendorSuspense(<SubscriptionPlansPage />)}
                              </MainLayout>
                            </FeatureGate>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/vendor/subscription/checkout"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR]}>
                            <FeatureGate
                              flag="vendor-billing"
                              fallback={<Navigate to="/vendor/venues" replace />}
                            >
                              <MainLayout>
                                {withVendorSuspense(<SubscriptionCheckoutPage />)}
                              </MainLayout>
                            </FeatureGate>
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="/vendor/billing-history"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR]}>
                            <FeatureGate
                              flag="vendor-billing"
                              fallback={<Navigate to="/vendor/venues" replace />}
                            >
                              <MainLayout>{withVendorSuspense(<BillingHistoryPage />)}</MainLayout>
                            </FeatureGate>
                          </RoleProtectedRoute>
                        }
                      />
                      {/* Vendor PC Analytics — per-venue, gated by pc-analytics capability */}
                      <Route
                        path="/venues/:venueId/analytics/pc"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>{withVendorSuspense(<PCAnalyticsPage />)}</MainLayout>
                          </RoleProtectedRoute>
                        }
                      />

                      {/* Vendor-scoped routes — nested under /vendor/:venueId/ */}
                      <Route
                        path="/vendor/:venueId"
                        element={
                          <RoleProtectedRoute requiredRoles={[Role.VENDOR, Role.VENDOR_STAFF]}>
                            <MainLayout>
                              <VenueLayout />
                            </MainLayout>
                          </RoleProtectedRoute>
                        }
                      >
                        <Route path="dashboard" element={withVendorSuspense(<VendorDashboard />)} />
                        <Route
                          path="bookings"
                          element={withVendorSuspense(<VendorBookingsListPage />)}
                        />
                        <Route
                          path="bookings/calendar"
                          element={withVendorSuspense(<VendorBookingsCalendarPage />)}
                        />
                        <Route
                          path="bookings/active-sessions"
                          element={withVendorSuspense(<ActiveSessionsPageWrapper />)}
                        />
                        <Route
                          path="bookings/:id"
                          element={withVendorSuspense(<VendorBookingDetailPage />)}
                        />
                        <Route
                          path="check-in"
                          element={withVendorSuspense(<VendorCheckInPage />)}
                        />
                        <Route
                          path="unpaid-bookings"
                          element={withVendorSuspense(<UnpaidBookingsPage />)}
                        />
                        <Route path="coupons" element={withVendorSuspense(<VendorCouponPage />)} />
                        <Route path="food-menu" element={withVendorSuspense(<VendorMenuPage />)} />
                        <Route
                          path="food-offers"
                          element={withVendorSuspense(<VendorOffersPage />)}
                        />
                        <Route
                          path="food-analytics"
                          element={withVendorSuspense(<FoodAnalyticsPage />)}
                        />
                        <Route
                          path="analytics"
                          element={withVendorSuspense(<VendorAnalyticsDashboardPage />)}
                        />
                        <Route
                          path="addon-analytics"
                          element={withVendorSuspense(<AddonAnalyticsPage />)}
                        />
                        <Route
                          path="customers"
                          element={withVendorSuspense(<VendorCustomersPage />)}
                        />
                        <Route
                          path="customers/:customerId"
                          element={withVendorSuspense(<VendorCustomerDetailPage />)}
                        />
                        <Route path="staff" element={withVendorSuspense(<StaffManagementPage />)} />
                        <Route path="payouts" element={withVendorSuspense(<VendorPayoutsPage />)} />
                        <Route
                          path="payouts/:payoutId"
                          element={withVendorSuspense(<VendorPayoutDetailPage />)}
                        />
                        <Route
                          path="tax-invoices"
                          element={withVendorSuspense(<TaxInvoicesPage />)}
                        />
                        <Route
                          path="commission-invoices"
                          element={withVendorSuspense(<CommissionInvoicesPage />)}
                        />
                        <Route
                          path="tax-summary"
                          element={withVendorSuspense(<VendorTaxSummaryPage />)}
                        />
                        <Route path="reviews" element={withVendorSuspense(<VendorReviewsPage />)} />
                        <Route
                          path="competitive"
                          element={
                            <FeatureGate
                              flag="competitive-leaderboard"
                              fallback={<Navigate to="../dashboard" replace />}
                            >
                              {withVendorSuspense(<VendorLeaderboardPage />)}
                            </FeatureGate>
                          }
                        />
                        <Route
                          path="pc-management/agent-devices"
                          element={withVendorSuspense(<AgentDevicesPage />)}
                        />
                        <Route
                          path="pc-management/pc-grid"
                          element={withVendorSuspense(<PCAvailabilityGrid />)}
                        />
                      </Route>

                      {/* Legacy redirects — old flat /vendor/* paths → /vendor/:venueId/* */}
                      <Route path="/vendor/dashboard" element={<VenueRedirect to="dashboard" />} />
                      <Route
                        path="/vendor/bookings/:bookingId"
                        element={<BookingDetailVenueRedirect />}
                      />
                      <Route path="/vendor/bookings" element={<VenueRedirect to="bookings" />} />
                      <Route
                        path="/vendor/bookings/calendar"
                        element={<VenueRedirect to="bookings/calendar" />}
                      />
                      <Route
                        path="/vendor/bookings/active-sessions"
                        element={<VenueRedirect to="bookings/active-sessions" />}
                      />
                      <Route path="/vendor/check-in" element={<VenueRedirect to="check-in" />} />
                      <Route
                        path="/vendor/unpaid-bookings"
                        element={<VenueRedirect to="unpaid-bookings" />}
                      />
                      <Route path="/vendor/coupons" element={<VenueRedirect to="coupons" />} />
                      <Route path="/vendor/food-menu" element={<VenueRedirect to="food-menu" />} />
                      <Route
                        path="/vendor/food-offers"
                        element={<VenueRedirect to="food-offers" />}
                      />
                      <Route
                        path="/vendor/food-analytics"
                        element={<VenueRedirect to="food-analytics" />}
                      />
                      <Route path="/vendor/analytics" element={<VenueRedirect to="analytics" />} />
                      <Route
                        path="/vendor/addon-analytics"
                        element={<VenueRedirect to="addon-analytics" />}
                      />
                      <Route path="/vendor/customers" element={<VenueRedirect to="customers" />} />
                      <Route path="/vendor/users" element={<VenueRedirect to="customers" />} />
                      <Route path="/vendor/staff" element={<VenueRedirect to="staff" />} />
                      <Route path="/vendor/payouts" element={<VenueRedirect to="payouts" />} />
                      <Route
                        path="/vendor/tax-invoices"
                        element={<VenueRedirect to="tax-invoices" />}
                      />
                      <Route
                        path="/vendor/commission-invoices"
                        element={<VenueRedirect to="commission-invoices" />}
                      />
                      <Route
                        path="/vendor/tax-summary"
                        element={<VenueRedirect to="tax-summary" />}
                      />
                      <Route path="/vendor/reviews" element={<VenueRedirect to="reviews" />} />
                      <Route
                        path="/vendor/pc-management/agent-devices"
                        element={<VenueRedirect to="pc-management/agent-devices" />}
                      />
                      <Route
                        path="/vendor/pc-management/pc-grid"
                        element={<VenueRedirect to="pc-management/pc-grid" />}
                      />

                      {/* Default Route - Landing Page */}
                      <Route
                        path="/"
                        element={
                          <PublicLayout>
                            <LandingPage />
                          </PublicLayout>
                        }
                      />

                      {/* 404 Not Found - Catch all unmatched routes */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                    <CookieConsent />
                    <UpdateNotification />
                    <VendorNotificationToasts />
                    <VendorDesktopNotificationPrompt />
                  </ErrorBoundary>
                </ToastProvider>
              </CityProvider>
            </AuthProvider>
          </BrowserRouter>
          <Toaster />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </LocalizationProvider>
    </ThemeContextProvider>
  );
}

export default App;
